/*
 * Figma `112:140` — SETUP-03 · Wristband · Registry + Create.
 *
 * `ManagementTemplate`: the registry beside the create panel, which is what the frame draws. The
 * screen is small, and two details are the whole reason it is worth rebuilding.
 *
 * **Each band says what it is bound to.** `GOLD · ทอง — VIP A · 324 sold · 2 ticket types` in the
 * frame; the old panel showed a name and a dot. A wristband is only consequential through its
 * bindings, and an organizer deciding whether to delete one cannot decide that from its name.
 *
 * **The delete confirmation counts those bindings first** (`112:317`), and says the thing that is
 * easy to get wrong in the other direction: removing a band unbinds it from every ticket type, and
 * changes nothing about orders already sold. People hesitate to delete because they fear the second
 * one; saying so is what makes the first one safe to do.
 *
 * Colours come from `--jt-band-*` rather than the status tones. These stand for a physical band an
 * usher tells apart on a wrist at night, and the status tones collide — yellow and orange are both
 * the warning colour, so two bands would render identically.
 */
import { useState } from 'react';
import {
  Alert, Button, Card, ConfirmDialog, ManagementTemplate, StatusBadge, TextField,
} from '..';
import type { Destination } from '../destinations';
import { WRISTBAND_COLORS } from '../../api';
import type { WristbandColor, WristbandDto } from '../../api';
import type { WristbandsState } from './useWristbands';
import { WRISTBAND_COLOR_LABEL, useWristbands } from './useWristbands';

function ColorPicker({ value, onChange, idPrefix }: {
  value: WristbandColor;
  onChange: (next: WristbandColor) => void;
  idPrefix: string;
}) {
  return (
    <div className="jt-field">
      <span className="jt-field-label" id={`${idPrefix}-label`}>สี</span>
      {/* A radio group: one colour is in effect, and arrow keys then move between them for free. */}
      <div className="jt-band-picker" role="radiogroup" aria-labelledby={`${idPrefix}-label`}>
        {WRISTBAND_COLORS.map((color) => {
          const on = color === value;
          return (
            <button
              key={color}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={WRISTBAND_COLOR_LABEL[color]}
              tabIndex={on ? 0 : -1}
              className="jt-band-swatch"
              data-color={color}
              data-selected={on || undefined}
              onClick={() => onChange(color)}
            />
          );
        })}
      </div>
    </div>
  );
}

function BandDot({ color }: { color: WristbandColor }) {
  return <span className="jt-band-dot" data-color={color} aria-hidden="true" />;
}

export function WristbandScreen({ destination, state, permitted, role }: {
  destination: Destination;
  state: WristbandsState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    wristbands, usage, loading, loadError, error, busyId,
    draft, setDraft, draftError, creating, create, rename, remove,
  } = state;
  const [confirmDelete, setConfirmDelete] = useState<WristbandDto | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const pending = confirmDelete ? usage(confirmDelete.id) : null;

  return (
    <ManagementTemplate
      gate={{ destination, loading, error: loadError || null, permitted, role }}
      summary={
        <>
          <Alert tone="info" title="การลบ Wristband ถอดออกจากประเภทบัตรที่ผูกไว้">
            ประเภทบัตรที่ผูกไว้จะไม่มีริสแบนด์นี้อีก แต่ออร์เดอร์ที่ขายไปแล้วไม่เปลี่ยน — ผู้ซื้อยังได้รับตามที่ซื้อไว้
          </Alert>
          {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
        </>
      }
      items={
        <div className="jt-registry-split jt-registry-split--wide">
          <Card title={`Wristband registry · ${wristbands.length}`} sub="ผูกกับประเภทบัตรได้ที่หน้า “บัตรและราคา”">
            {wristbands.length === 0 && (
              <p className="jt-field-hint">ยังไม่มี Wristband — สร้างทางขวาแล้วค่อยนำไปผูกกับประเภทบัตร</p>
            )}
            <ul className="jt-band-list">
              {wristbands.map((band) => {
                const use = usage(band.id);
                const editing = editingId === band.id;
                return (
                  <li key={band.id} className="jt-band-row">
                    <BandDot color={band.color} />
                    <div className="jt-band-copy">
                      {editing ? (
                        <TextField
                          label={`ชื่อ ${band.name}`}
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                        />
                      ) : (
                        <>
                          <span className="jt-band-name">
                            {band.name} · {WRISTBAND_COLOR_LABEL[band.color]}
                          </span>
                          <span className="jt-band-usage">
                            {use.ticketNames.length === 0
                              ? 'ยังไม่ได้ผูกกับประเภทบัตรใด'
                              : `${use.ticketNames.join(' · ')} · ขายแล้ว ${use.sold.toLocaleString('th-TH')} ใบ · ${use.ticketNames.length} ประเภทบัตร`}
                          </span>
                        </>
                      )}
                      {editing && (
                        <ColorPicker
                          value={band.color}
                          onChange={(color) => void rename(band.id, { color })}
                          idPrefix={`band-${band.id}`}
                        />
                      )}
                    </div>
                    <div className="jt-inline-actions">
                      {use.ticketNames.length === 0
                        ? <StatusBadge tone="neutral">ยังไม่ได้ใช้</StatusBadge>
                        : <StatusBadge tone="accent">{`${use.bands.toLocaleString('th-TH')} เส้น`}</StatusBadge>}
                      {editing ? (
                        <>
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={busyId === band.id || !editName.trim()}
                            onClick={async () => { await rename(band.id, { name: editName.trim() }); setEditingId(null); }}
                          >
                            บันทึก
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>ยกเลิก</Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingId(band.id); setEditName(band.name); }}
                          >
                            แก้ไข
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyId === band.id}
                            onClick={() => setConfirmDelete(band)}
                          >
                            ลบ
                          </Button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card title="สร้าง Wristband ใหม่" sub="สร้างแล้วไปผูกกับประเภทบัตรใน “บัตรและราคา”">
            {draftError && <Alert tone="danger" title="สร้างไม่สำเร็จ">{draftError}</Alert>}
            <TextField
              label="ชื่อ Wristband"
              requiredMark
              value={draft.name}
              placeholder="เช่น STAFF, VIP, โซน A"
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <ColorPicker value={draft.color} onChange={(color) => setDraft({ ...draft, color })} idPrefix="band-new" />
            <div className="jt-inline-actions">
              <Button variant="primary" disabled={creating || !draft.name.trim()} onClick={() => void create()}>
                {creating ? 'กำลังสร้าง…' : 'สร้าง Wristband'}
              </Button>
            </div>
            <Alert tone="info" title="ก่อนลบ ระบบจะบอกว่าผูกอยู่กับอะไรบ้าง">
              จำนวนประเภทบัตรที่ผูกไว้และจำนวนที่ขายไปแล้วจะแสดงในหน้ายืนยัน เพื่อให้เห็นผลกระทบก่อนตัดสินใจ
            </Alert>
          </Card>
        </div>
      }
      detail={
        <ConfirmDialog
          open={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            const target = confirmDelete;
            setConfirmDelete(null);
            if (target) void remove(target.id);
          }}
          title={`ลบ Wristband ${confirmDelete?.name ?? ''}`}
          confirmLabel="ลบ Wristband นี้"
          busy={!!confirmDelete && busyId === confirmDelete.id}
          consequences={
            pending && pending.ticketNames.length > 0
              ? [
                `ถอดออกจาก ${pending.ticketNames.length} ประเภทบัตร: ${pending.ticketNames.join(', ')}`,
                `${pending.sold.toLocaleString('th-TH')} ใบที่ขายไปแล้วไม่เปลี่ยน — ผู้ซื้อยังได้รับตามที่ซื้อไว้`,
                'ประเภทบัตรเหล่านั้นจะไม่แถมริสแบนด์นี้กับออร์เดอร์ใหม่อีก',
                'การลบย้อนกลับไม่ได้ — ถ้าต้องใช้อีกต้องสร้างใหม่และผูกกับบัตรใหม่ทั้งหมด',
              ]
              : [
                'ริสแบนด์นี้ยังไม่ได้ผูกกับประเภทบัตรใด จึงไม่มีออร์เดอร์ใดได้รับผลกระทบ',
                'การลบย้อนกลับไม่ได้ — ถ้าต้องใช้อีกต้องสร้างใหม่',
              ]
          }
        />
      }
    />
  );
}

export function WristbandDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useWristbands(token, eventSlug);
  return <WristbandScreen destination={destination} state={state} permitted={permitted} role={role} />;
}
